import { LitElement, html, property } from 'lit-element'
import { bemBlock } from '@polleverywhere/bem-helpers'
import { contextualize, cutlassEvent } from '@polleverywhere/cutlass'
import { defineElementSlot } from '@polleverywhere/element-helpers'
import { register } from '@polleverywhere/hmr-custom-element'
import '@polleverywhere/pe-button'
import '@polleverywhere/pe-graphic'
import './item'
import './panel.scss'

/**
 * Nav panel
 *
 * Renders a navigation panel that is composed of a heading and additional
 * navigation sections of items separated by dividers.
 *
 * @element pe-navigation-panel
 *
 * @slot - Default slot containing navigation items and dividers.
 *
 * @prop {string} active - The current active navigation item in the panel.
 * @prop {HTMLElement} activeItem - (READONLY) Returns the currently active item.
 * @prop {string} heading - The heading to display on top of the panel.
 * @prop {string} icon - The optional icon for the heading.
 * @prop {boolean} showToggle - Controls whether the panel can be opened/closed
 * @prop {boolean} opened - The state of the panel; defaults to false (closed)
 *
 * @cssprop --pe-navigation-color - Foreground color
 * @cssprop --pe-navigation-panel-bg-color - Background color for L2
 * @cssprop --pe-navigation-panel-active-bg-color - Active background color for
 *  L2
 * @cssprop --pe-navigation-panel-max-width - Max width for the L2 panel to stretch
 *  out to when it contains longer nav items.
 * @cssprop --pe-navigation-panel-min-width - Min width for the L2 panel to shrink to
 *  when nav items are short.
 *  section divider.
  *
 * @fires {Object} activating - Fires when activating a new navigation item.
 * @fires {Object} activated - Fires when active navigation item changes.
 */
export default class Panel extends LitElement {
  static get tagName () { return 'pe-navigation-panel' }

  @property({ type: String })
  get active () {
    return this._active
  }

  set active (value) {
    const oldValue = this._active
    if (oldValue === value) return

    // Inform consumers that a new item has been activated.
    const activatingItem = this.querySelector(`pe-navigation-item[name='${value}']`)
    const cancelled = !this.dispatchEvent(new CustomEvent('activating', {
      bubbles: true,
      cancelable: true,
      detail: {
        item: activatingItem
      }
    }))
    if (cancelled) return

    this._active = value
    this.requestUpdate('active', oldValue)
    updateActiveItem.call(this)

    // Inform consumers that the active item changed.
    this.dispatchEvent(new CustomEvent('activated', { bubbles: true }))

    // Track event only when activated state changes, i.e. not on initial load.
    if (value) {
      cutlassEvent(this, ['activated', value].join(':'))
    }
  }

  get activeItem () {
    return Array.from(this.querySelectorAll('pe-navigation-item'))
      .find((item) => item.active)
  }

  @property({ type: String })
  heading

  @property({ type: String })
  icon

  @property({ type: Boolean, attribute: 'show-toggle' })
  showToggle = false

  @property({ type: Boolean })
  opened = false

  constructor () {
    super()
    contextualize(this, this.tagName)
  }

  connectedCallback () {
    super.connectedCallback()
    defineElementSlot(this)
  }

  createRenderRoot () { return this }

  firstUpdated (changed) {
    updateActiveItem.call(this)
  }

  updated (changedMap) {
    if (changedMap.has('opened') &&
      !!changedMap.get('opened') !== this.opened) {
      // Track opened/closed state but not on its initial render.
      const eventName = this.opened ? 'opened' : 'closed'
      cutlassEvent(this, eventName)
    }
  }

  render () {
    const mods = {
      showToggle: this.showToggle,
      opened: this.opened
    }

    return html`
    <div @navigate=${handleNavigate} class=${bem(mods)}>
      ${renderTogglePanel.call(this)}
      <div class=${bem('content')}>
        <h2 class=${bem('heading')}>
          ${renderHeadingIcon.call(this)}
          <span>${this.heading}</span>
          ${renderToggleControl.call(this)}
        </h2>
        ${this.slots()}
      </div>
    </div>
    `
  }
}

function handleNavigate (e) {
  // Only update the active nav item if the item is not a redirect.
  if (!e.target.href) {
    this.active = e.target.name
  }
}

function updateActiveItem () {
  this.querySelectorAll('pe-navigation-item').forEach(item => {
    item.active = item.name && item.name === this.active
  })
}

function renderTogglePanel () {
  if (!this.showToggle) return

  return html`
  <div @click=${handleToggle} class=${bem('toggle-panel')}>
    <div class=${bem('highlight')}></div>
    <pe-button
        class=${bem('open-button')}
        icon='arrow-right'
        name='open'
        description='Open navigation panel'
        size='compact'
        type='link'
    ></pe-button>
  </div>
  `
}

function renderHeadingIcon () {
  if (!this.icon) return
  return html`
  <pe-graphic
      class=${bem('heading-icon')}
      name=${this.icon}
      type='icon'
  ></pe-graphic>
  `
}

function renderToggleControl () {
  if (!this.showToggle) return

  return html`
  <pe-button
      @click=${handleToggle}
      class=${bem('close-button')}
      icon='arrow-left'
      name='close'
      description='Close navigation panel'
      size='compact'
      type='ghost'
  ></pe-button>
  `
}

function handleToggle (e) {
  this.opened = !this.opened
}

const bem = bemBlock(Panel.tagName)
register(module, Panel.tagName, Panel)
